import type { TowerType } from "@mmotdg/shared";
import * as THREE from "three";
import { buildTowerModel, disposeTowerModel } from "../game/models/towers/index.js";

const CACHE = new Map<string, string>();
const THUMB_SIZE = 96;

let renderer: THREE.WebGLRenderer | null = null;
let scene: THREE.Scene | null = null;
let camera: THREE.PerspectiveCamera | null = null;

function ensureRenderer(): {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
} | null {
  if (typeof document === "undefined") return null;
  if (renderer && scene && camera) return { renderer, scene, camera };
  try {
    renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio ?? 1, 2));
    renderer.setSize(THUMB_SIZE, THUMB_SIZE, false);
    renderer.setClearColor(0x000000, 0);

    scene = new THREE.Scene();

    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambient);
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(2.5, 4, 3);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x6ee7ff, 0.5);
    rim.position.set(-3, 2, -2);
    scene.add(rim);

    camera = new THREE.PerspectiveCamera(32, 1, 0.1, 20);
    camera.position.set(1.6, 1.55, 1.6);
    camera.lookAt(0, 0.55, 0);

    return { renderer, scene, camera };
  } catch {
    renderer = null;
    scene = null;
    camera = null;
    return null;
  }
}

export function getTowerThumbnail(type: TowerType): string | null {
  const cached = CACHE.get(type);
  if (cached) return cached;

  const ctx = ensureRenderer();
  if (!ctx) return null;

  const view = buildTowerModel(type, { level: 1, withSelectionRing: false });
  view.group.position.y = 0;
  view.group.rotation.y = Math.PI / 5;
  ctx.scene.add(view.group);

  ctx.renderer.render(ctx.scene, ctx.camera);
  const url = ctx.renderer.domElement.toDataURL("image/png");

  ctx.scene.remove(view.group);
  disposeTowerModel(view);

  CACHE.set(type, url);
  return url;
}

export function towerThumbnailHtml(
  type: TowerType,
  options: { size?: number; muted?: boolean } = {},
): string {
  const size = options.size ?? 28;
  const url = getTowerThumbnail(type);
  const mutedClass = options.muted ? " is-muted" : "";
  if (!url) {
    return `<span class="tower-thumb${mutedClass}" style="width:${size}px;height:${size}px;"></span>`;
  }
  return `<img class="tower-thumb${mutedClass}" src="${url}" alt="${type}" width="${size}" height="${size}" loading="lazy" />`;
}
